
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradingCentralSettings struct
 */
class TradingCentralSettings {
  /**
   * Initialize struct
   * @param {!boolean=} enabled
   * @param {!string=} clientId
   * @param {!string=} password
   * @constructor
   */
  constructor (enabled = false, clientId = '', password = '') {
    this.Enabled = enabled
    this.ClientId = clientId
    this.Password = password
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingCentralSettings}
   * @param {!TradingCentralSettings} other Other struct
   * @returns {!TradingCentralSettings} This struct
   */
  copy (other) {
    if (other.Enabled != null) {
      this.Enabled = other.Enabled
    } else {
      this.Enabled = null
    }
    if (other.ClientId != null) {
      this.ClientId = other.ClientId
    } else {
      this.ClientId = null
    }
    if (other.Password != null) {
      this.Password = other.Password
    } else {
      this.Password = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingCentralSettings}
   * @returns {!TradingCentralSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingCentralSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingCentralSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingCentralSettings}
   * @param {!TradingCentralSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingCentralSettings)) {
      throw new TypeError('Instance of TradingCentralSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingCentralSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Enabled: ((this.Enabled != null) ? this.Enabled : null),
      ClientId: ((this.ClientId != null) ? this.ClientId : null),
      Password: ((this.Password != null) ? this.Password : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingCentralSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingCentralSettings} other Object value
   * @returns {!TradingCentralSettings} Created struct
   */
  static fromObject (other) {
    return new TradingCentralSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingCentralSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingCentralSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingCentralSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 195
  }
}

export { TradingCentralSettings };

/**
 * Fast Binary Encoding TradingCentralSettings field model
 */
class FieldModelTradingCentralSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Enabled = new fbe.FieldModelBool(buffer, 4 + 4)
    this.ClientId = new fbe.FieldModelString(buffer, this.Enabled.fbeOffset + this.Enabled.fbeSize)
    this.Password = new fbe.FieldModelString(buffer, this.ClientId.fbeOffset + this.ClientId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingCentralSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingCentralSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Enabled.fbeSize + this.ClientId.fbeSize + this.Password.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingCentralSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Enabled.fbeExtra + this.ClientId.fbeExtra + this.Password.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingCentralSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingCentralSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingCentralSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 195
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingCentralSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingCentralSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Enabled.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Enabled.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Enabled.fbeSize

    if ((fbeCurrentSize + this.ClientId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ClientId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ClientId.fbeSize

    if ((fbeCurrentSize + this.Password.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Password.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Password.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingCentralSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingCentralSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingCentralSettings}
   * @param {!TradingCentralSettings} fbeValue Default value, defaults is new TradingCentralSettings()
   * @returns {!TradingCentralSettings} TradingCentralSettings value
   */
  get (fbeValue = new TradingCentralSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingCentralSettings}
   * @param {!TradingCentralSettings} fbeValue TradingCentralSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Enabled.fbeSize) <= fbeStructSize) {
      fbeValue.Enabled = this.Enabled.get()
    } else {
      fbeValue.Enabled = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Enabled.fbeSize

    if ((fbeCurrentSize + this.ClientId.fbeSize) <= fbeStructSize) {
      fbeValue.ClientId = this.ClientId.get()
    } else {
      fbeValue.ClientId = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ClientId.fbeSize

    if ((fbeCurrentSize + this.Password.fbeSize) <= fbeStructSize) {
      fbeValue.Password = this.Password.get()
    } else {
      fbeValue.Password = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Password.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingCentralSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingCentralSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingCentralSettings}
   * @param {!TradingCentralSettings} fbeValue TradingCentralSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingCentralSettings}
   * @param {TradingCentralSettings} fbeValue TradingCentralSettings value
   */
  setFields (fbeValue) {
    this.Enabled.set(fbeValue.Enabled)
    this.ClientId.set(fbeValue.ClientId)
    this.Password.set(fbeValue.Password)
  }
}

export { FieldModelTradingCentralSettings };

/**
 * Fast Binary Encoding TradingCentralSettings model
 */
class TradingCentralSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingCentralSettings(this.buffer, 4)
  }

  /**
   * Get the TradingCentralSettings model
   * @this {!TradingCentralSettingsModel}
   * @returns {!FieldModelTradingCentralSettings} model TradingCentralSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingCentralSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingCentralSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingCentralSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingCentralSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingCentralSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingCentralSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingCentralSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingCentralSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingCentralSettingsModel}
   * @param {!TradingCentralSettings} value TradingCentralSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingCentralSettingsModel}
   * @param {!TradingCentralSettings} value TradingCentralSettings value, defaults is new TradingCentralSettings()
   * @return {!object} Deserialized TradingCentralSettings value and its size
   */
  deserialize (value = new TradingCentralSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingCentralSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingCentralSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingCentralSettingsModel}
   * @param {!number} prev Previous TradingCentralSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingCentralSettingsModel };
